/**
 * WIZARD_TIPS — Sprint 6 · Tooltips didácticos
 *
 * Explicaciones con ejemplos numéricos para los campos que a los inversionistas
 * les cuesta más. Estos sobreescriben las descripciones técnicas del catalog.
 */
export const WIZARD_TIPS = {
  // ── Financiamiento — Crédito del proyecto ──
  credit_line_amount:
    'Monto máximo autorizado por el banco para tu proyecto. Es el techo contractual. Ejemplo: $30,000,000 significa que nunca podrás disponer más de eso, sin importar el avance.',
  credit_aforo_pct:
    'Porcentaje del presupuesto autorizado que el banco financia. Es lo que define tu línea y lo declara el banco: sin aforo, el motor no dispone crédito y avisa. Con 65% y un presupuesto de $10M, el banco pone $6.5M y el resto lo pones tú.',
  credit_ltv_max:
    'Aforo de garantía (cobertura). Razón de cobertura que exige el banco sobre el valor de las garantías. Desde SWA-54 se vigila y se avisa, pero NO limita la ministración: el que gobierna la línea es el aforo del crédito.',
  credit_advance_pct:
    'Porcentaje de la línea que el banco te entrega en el mes 1 como anticipo (típico 10-20%). Ejemplo: línea $20M con 15% = $3M al arrancar. Se entrega íntegro, sin pasar por el aforo del avance.',
  credit_advance_amount:
    'Anticipo en pesos, cuando el banco te lo dio como monto y no como porcentaje. Manda sobre el porcentaje si ambos son > 0. Ejemplo: $3,000,000 de una línea de $12,000,000.',
  credit_advance_treatment:
    'Cómo recupera el banco el anticipo. Se amortiza (default): cada ministración retiene la parte del anticipo que corresponde al avance del mes, hasta absorberlo. Consume línea: el anticipo reduce la línea disponible y las ministraciones reponen el avance completo.',
  ministration_lag_months:
    'Meses entre que compruebas el avance y el banco deposita. 0 = mismo mes. Típico 1-2 meses: si valúas en enero y el rezago es 1, cobras en febrero.',
  loan_term_months:
    'Duración total del crédito (desde disposición hasta vencimiento). Puentes típicos: 24-36 meses. Créditos individuales de compradores: 240 meses (20 años).',
  loan_grace_months:
    'Meses iniciales donde sólo pagas intereses (no capital). Ejemplo: 6 meses de gracia en un crédito de 30 = 24 meses efectivos de amortización.',
  loan_disbursement_month:
    'Mes en que el banco te desembolsa el crédito por primera vez. Normalmente 1 (arranque) pero puede ser posterior si hay preventa previa.',
  bridge_repayment_mode:
    'Cómo se paga el principal. LPU = por unidad entregada (típico puente residencial). Bullet = todo al vencimiento (típico terreno). Mixto = LPU mientras vendas + residuo bullet. Sweep = barrido oportunista de caja.',
  bridge_lpu_amount:
    'Monto de liberación por unidad. Ejemplo: crédito $20M / 20 unidades = $1M por unidad. Al vender una vivienda, $1M se paga al banco.',
  bridge_maturity_month:
    'Mes en el que el banco exige el pago completo del residuo (bullet). Se calcula como disposición + plazo - 1 si no se declara aquí.',
  custom_ministration_json:
    'Calendario EXACTO que te dio el banco de cuándo y cuánto te va a ministrar. Sobrescribe la curva teórica. Formato: [{"month":1,"amount":5000000}, ...]. Suma ≤ línea autorizada.',
  loan_interest_rate_annual:
    'Tasa anual del crédito. En México los puentes típicos van a TIIE + 400-800 bps (11-15% anual). El motor la aplica mensualmente sobre el saldo.',

  // ── Financiamiento — Equity ──
  equity_pct:
    'Porcentaje del costo total que aportas de capital propio. El resto lo cubre el crédito. Ejemplo: 30% equity + 70% crédito. Si declaras equity_schedule, este porcentaje deja de importar.',
  equity_budget_absolute:
    'Monto absoluto de equity comprometido (override sobre equity_pct). Útil cuando ya sabes exactamente cuánto pondrás. -1 = usar equity_pct.',
  equity_schedule_json:
    'Aportes reales de capital mes a mes. Sustituye el hack de forzar equity_pct=0. Ejemplo: [{"month":1,"amount":2000000},{"month":6,"amount":3000000}]. Si el proyecto necesita más, el motor lo marca como equity overflow (alerta).',

  // ── Aforo (LTC/LTV) — recordatorio en waterfall ──
  target_irr:
    'Retorno mínimo aceptable (hurdle). Convención institucional en MX: 18-25% anual. Se usa como referencia para el Tier 2 promote del waterfall y para las alertas de rentabilidad.',
  discount_rate:
    'Descuenta el VAN del proyecto: terreno, obra y venta, sin crédito. Refleja el costo de capital del proyecto. Default 12 %. Mes 1 = hoy.',
  equity_required_return:
    'Descuenta el VAN del inversionista: aportaciones y distribuciones. Normalmente es mayor que la tasa del proyecto: es lo que exiges a tu dinero por asumir el riesgo después del banco. 0 = igual a la tasa del proyecto.',
  mirr_reinvest_rate:
    'Tasa a la que se reinvierten las distribuciones que recibes antes del cierre, para calcular la MIRR. 0 = igual a la tasa del proyecto. Si ese dinero se quedaría en Cetes o en la cuenta de la empresa, captura esa tasa.',

  // ── Ventas y cobranza ──
  presale_pct:
    'Porcentaje de unidades que planeas vender ANTES de arrancar obra. Ejemplo: 30% de 20 unidades = 6 vendidas en preventa. Motor asume 3 meses de ventana si dejas presale_months=0.',
  presale_months:
    'Duración de la ventana de preventa (antes de construcción). Típico 3-6 meses. 0 = usar default automático de 3.',
  absorption_rate_units_per_month:
    'Velocidad de venta mensual esperada. Ejemplo: 2.5 uds/mes en 20 unidades = 8 meses de comercialización.',
  first_delivery_pct:
    'Porcentaje de unidades entregadas en el primer mes de entregas. Ejemplo: 30% en 20 uds = 6 unidades. El resto se distribuye linealmente.',
  first_delivery_month:
    'Mes en que empiezan a entregarse unidades a compradores (típicamente al 60-80% de avance de obra).',
  delivery_lag_months:
    'Rezago entre entrega física y pago final del comprador (típico 1-3 meses por trámite notarial y hipoteca).',
  enganche_pct:
    'Porcentaje del precio pagado como enganche antes de la escrituración. Típico 15-30% en residencial.',
  parcialidades_pct:
    'Porcentaje pagado en mensualidades entre apartado y escrituración. Suma con enganche + escrituración debe dar 100%.',
  pago_escrituracion_pct:
    'Porcentaje pagado a escrituración (típicamente vía crédito hipotecario del comprador). En vivienda residencial va del 60-80%.',
  credito_hipotecario_pct:
    'Porcentaje de compradores que pagan con crédito hipotecario. Modeled: 1 mes de trámite adicional vs contado.',

  // ── Costos y contingencia ──
  contingency_pct:
    'Reserva sobre costos duros para imprevistos. Recomendación institucional: 5-8%. Se aplica al presupuesto directo (edificación + urbanización).',
  expected_overrun_pct:
    'Sobrecosto ESPERADO (independiente de la contingencia). Ejemplo: si el mercado tiene inflación de costos, 3-5% adicional. Se manifiesta como cost overrun report.',
  construction_inflation_pct:
    'Inflación anual esperada sobre costos duros. Típico 5-8% en México. Motor la aplica mes a mes de forma compuesta.',
  cancellation_rate_pct:
    'Porcentaje de ventas que se caen antes de escriturar. Típico 5–15 % según segmento; 0 % supone que ninguna venta se cae.',
  price_escalation_pct:
    'Escalación anual del precio de venta. Debería ir por arriba de la inflación de costos para preservar margen (típico 6-10%).',

  // ── Pasivos ──
  existing_loan_balance:
    'CAMPO LEGACY DEPRECADO. Migra a la colección estructurada de pasivos con "Migrar pasivo legacy". Se removerá en v3.0.',
};

/**
 * ADVANCED_KEYS — campos que se ocultan en Modo Guiado.
 * Estos son variables técnicas raramente ajustadas por usuarios nuevos.
 * En Modo Power todo se muestra.
 */
export const ADVANCED_KEYS = new Set([
  // JSONs raw (mejor UX cuando aplique via componentes dedicados)
  'custom_ministration_json',
  'bridge_repayment_schedule_json',
  'equity_schedule_json',
  'custom_sales_monthly_json',
  'tiie_curve_monthly_json',
  'collection_segments_json',
  // Overrides finos
  'credit_ltv_max',
  'ministration_lag_months',
  'bridge_maturity_month',
  'bridge_lpu_amount',
  'hard_cost_total_override',
  'indirect_cost_total_override',
  'equity_budget_absolute',
  'equity_limit',
  // TIIE variable (Sprint 5 territory)
  'tiie_base_rate',
  'tiie_spread',
  'tiie_annual_change',
  'interest_rate_mode',
  // Sensibilidades (mejor via panel Stress)
  'sensitivity_price_range',
  'sensitivity_cost_range',
  'sensitivity_rate_range',
  'sensitivity_absorption_range',
  // Legacy
  'existing_loan_balance',
  // Fees marginales
  'comision_apertura_semestral',
  'comision_no_disposicion_pct',
  'gastos_notariales_pct',
  'seguros_ruv_pct',
  // Fine tuning
  'delinquency_rate_pct',
  'commercial_incentive_pct',
  'construction_sales_factor',
  'buffer_months',
  'post_construction_months',
  'price_escalation_frequency',
]);

/**
 * FIELD_EFFECT_NOTES — SWA-196 · Pasada 1
 *
 * Rótulo honesto para los campos capturables que el motor financiero NO lee.
 * Tres tratamientos, deliberadamente distintos:
 *
 *  kind='ficha'     → dato del predio / normativa. Correcto de llenar, no es un
 *                     defecto: el proyecto se asume autorizado y estos datos
 *                     describen el desarrollo, no lo calculan. Tono neutro.
 *  kind='pending'   → el campo promete afectar el resultado y no lo hace.
 *                     Funcionalidad no implementada. Aviso explícito.
 *  kind='schedule'  → el motor SÍ calcula el calendario, pero con otros campos.
 *                     El aviso dice cuáles, para que el usuario sepa dónde ir.
 *
 * NO cambia ningún cálculo. Solo informa.
 */
const FICHA_NOTE =
  'Dato de ficha · describe el proyecto y se guarda con el escenario. No participa en el cálculo financiero (hoy tampoco se imprime en los reportes).';

const CALENDAR_CORE_NOTE =
  'El motor no usa este mes. El calendario real lo arma con Meses de preventa + Meses de construcción + Meses post-construcción: corrígelo ahí.';

const CALENDAR_DELIVERY_NOTE =
  'El motor no usa este mes. Las entregas las gobiernan Modo de entrega, Inicio entregas (% avance) o (mes) y Rezago de entrega: corrígelo ahí.';

export const FIELD_EFFECT_NOTES = {
  // ── TRATAMIENTO 1 · informativos (ficha técnica del predio) ──
  total_land_area_m2:      { kind: 'ficha', text: FICHA_NOTE },
  total_buildable_area_m2: { kind: 'ficha', text: FICHA_NOTE },
  total_common_area_m2:    { kind: 'ficha', text: FICHA_NOTE },
  total_floors:            { kind: 'ficha', text: FICHA_NOTE },
  total_basements:         { kind: 'ficha', text: FICHA_NOTE },
  cos:                     { kind: 'ficha', text: FICHA_NOTE },
  cus:                     { kind: 'ficha', text: FICHA_NOTE },

  // ── TRATAMIENTO 2 · promesa rota (funcionalidad no implementada) ──
  // SWA-198 · los cuatro sensitivity_*_range salieron de esta lista: la corrida
  // ya los lee (escenario > compañía > default del catálogo).
  // SWA-126 · comision_apertura_pct y gastos_notariales_pct salieron de esta lista:
  // el motor 2.9.25 los cobra sobre la línea en el mes de la primera disposición.
  seguros_ruv_pct: {
    kind: 'pending',
    text: 'No implementado · el motor todavía no cobra seguros ni RUV. El valor se guarda, pero no afecta el flujo.',
  },
  hard_cost_per_m2: {
    kind: 'pending',
    text: 'No implementado · el costo duro se calcula con Costo duro por unidad o con el override total. Este valor por m² se guarda, pero no se usa.',
  },
  soft_cost_estudios_pct: {
    kind: 'pending',
    text: 'No implementado · el motor aplica el blando agregado (Costos blandos %). Este desglose se guarda, pero no se usa.',
  },
  soft_cost_licencias_pct: {
    kind: 'pending',
    text: 'No implementado · el motor aplica el blando agregado (Costos blandos %). Este desglose se guarda, pero no se usa.',
  },
  indirect_cost_total_override: {
    kind: 'pending',
    text: 'No implementado · el override no se lee. Para declarar indirectos exactos usa el modo detallado por partidas.',
  },
  indirect_cost_pct_revenue: {
    kind: 'pending',
    text: 'No implementado · el motor calcula indirectos con las partidas o con los montos mensuales. Este porcentaje se guarda, pero no se usa.',
  },
  subsidio_pct: {
    kind: 'pending',
    text: 'No implementado · la cobranza aún no se segmenta por fuente de pago. El motor usa la cascada apartado → enganche → parcialidades → escrituración.',
  },
  credito_hipotecario_pct: {
    kind: 'pending',
    text: 'No implementado · la cobranza aún no se segmenta por fuente de pago. El motor usa la cascada apartado → enganche → parcialidades → escrituración.',
  },
  contado_pct: {
    kind: 'pending',
    text: 'No implementado · la cobranza aún no se segmenta por fuente de pago. El motor usa la cascada apartado → enganche → parcialidades → escrituración.',
  },

  // ── TRATAMIENTO 3 · contradictorio (el calendario sale de otros campos) ──
  construction_start_month:      { kind: 'schedule', text: CALENDAR_CORE_NOTE },
  construction_end_month:        { kind: 'schedule', text: CALENDAR_CORE_NOTE },
  commercialization_start_month: { kind: 'schedule', text: CALENDAR_CORE_NOTE },
  delivery_start_month:          { kind: 'schedule', text: CALENDAR_DELIVERY_NOTE },
  delivery_months:               { kind: 'schedule', text: CALENDAR_DELIVERY_NOTE },
};

export function getFieldEffectNote(aKey) {
  return FIELD_EFFECT_NOTES[aKey] || null;
}

/**
 * Devuelve el tooltip enriquecido para una assumption. Prioridad:
 *  1. tooltip prop explícito (uso puntual)
 *  2. WIZARD_TIPS (didáctico Sprint 6)
 *  3. detail.description (descripción técnica del catalog)
 */
export function getTooltip(aKey, explicitTooltip, catalogDescription) {
  if (explicitTooltip) return explicitTooltip;
  if (WIZARD_TIPS[aKey]) return WIZARD_TIPS[aKey];
  return catalogDescription || '';
}

/**
 * Modo del Wizard: guiado o power.
 * Persiste en localStorage bajo 'stratika_wizard_mode'.
 */
export const WIZARD_MODE_KEY = 'stratika_wizard_mode';
export function getWizardMode() {
  if (typeof window === 'undefined') return 'guided';
  return localStorage.getItem(WIZARD_MODE_KEY) || 'guided';
}
export function setWizardMode(mode) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(WIZARD_MODE_KEY, mode);
  window.dispatchEvent(new CustomEvent('wizard-mode-change', { detail: mode }));
}
export function isAdvancedField(aKey) {
  return ADVANCED_KEYS.has(aKey);
}
